import { useEffect, useState } from "react";
import client, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ChecklistFieldEditor } from "@/components/forms/ChecklistFieldEditor";

const uid = () => Math.random().toString(36).slice(2, 9);
export const EMPTY_CHECKLIST = { name: "", scope: "job", jobTypes: [], instructions: "", sections: [{ id: uid(), title: "", order: 1, items: [] }] };

const newItem = (type = "checkbox") => ({ id: uid(), type, label: "", required: false, options: [] });

// Full-page checklist builder: name, job types, sections and fields.
export const ChecklistBuilder = ({ open, onOpenChange, editingId, initial, onSaved }) => {
  const [form, setForm] = useState(EMPTY_CHECKLIST);
  const [jobTypes, setJobTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setForm(initial || { ...EMPTY_CHECKLIST, sections: [{ id: uid(), title: "", order: 1, items: [newItem()] }] }); } }, [open, initial]);
  useEffect(() => { client.get("/job-types").then((r) => setJobTypes(r.data || [])).catch(() => {}); }, []);

  const setSection = (si, patch) => setForm((f) => ({ ...f, sections: f.sections.map((s, i) => (i === si ? { ...s, ...patch } : s)) }));
  const setItems = (si, fn) => setForm((f) => ({ ...f, sections: f.sections.map((s, i) => (i === si ? { ...s, items: fn(s.items) } : s)) }));
  const move = (arr, i, d) => { const a = [...arr]; [a[i], a[i + d]] = [a[i + d], a[i]]; return a; };
  const toggleType = (v) => setForm((f) => ({ ...f, jobTypes: f.jobTypes.includes(v) ? f.jobTypes.filter((x) => x !== v) : [...f.jobTypes, v] }));

  const save = async (publish) => {
    setBusy(true);
    try {
      const payload = { ...form, sections: form.sections.map((s, i) => ({ ...s, order: i + 1, items: s.items.map((it, j) => ({ ...it, order: j + 1 })) })) };
      const { data } = editingId ? await client.put(`/forms/templates/${editingId}`, payload) : await client.post("/forms/templates", payload);
      if (publish) await client.post(`/forms/templates/${data.id}/publish`);
      toast.success(publish ? `"${form.name}" saved and published` : `"${form.name}" saved as draft`);
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="form-builder-dialog" className="block max-w-none w-screen h-[100dvh] max-h-[100dvh] sm:rounded-none overflow-y-auto p-0 border-0">
        <div className="mx-auto w-full max-w-3xl px-5 py-8 sm:px-10 sm:py-10 space-y-6">
          <DialogHeader>
            <DialogTitle className="text-2xl sm:text-3xl pr-8">{editingId ? "Edit checklist" : "New checklist"}</DialogTitle>
            <DialogDescription>Give it a name, choose which job types get it automatically, then add your fields.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Checklist name</Label>
            <Input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. End of Tenancy checklist" className="h-11 text-base" />
          </div>
          <div className="space-y-2">
            <Label>Add automatically to these job types</Label>
            <div className="flex flex-wrap gap-2" data-testid="form-jobtypes">
              {jobTypes.map((jt) => {
                const on = form.jobTypes.includes(jt.value);
                return (
                  <button key={jt.value} type="button" onClick={() => toggleType(jt.value)} data-testid={`form-jobtype-${jt.value}`}
                    className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${on ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-brand/50"}`}>{jt.label}</button>
                );
              })}
            </div>
            <p className="text-xs text-slate-500">You can still add or remove it on any single job.</p>
          </div>
          <div className="space-y-2">
            <Label>Instructions for the cleaner (optional)</Label>
            <Textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} rows={2} data-testid="form-instructions" />
          </div>
          {form.sections.map((s, si) => (
            <div key={s.id} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3" data-testid={`form-section-${si}`}>
              <div className="flex items-center gap-2">
                <Input data-testid={`form-section-title-${si}`} placeholder={`Section ${si + 1} title (e.g. Kitchen)`} value={s.title} onChange={(e) => setSection(si, { title: e.target.value })} className="h-10 font-medium" />
                {form.sections.length > 1 && <Button size="icon" variant="ghost" className="text-red-500" onClick={() => setForm((f) => ({ ...f, sections: f.sections.filter((_, i) => i !== si) }))} data-testid={`form-section-delete-${si}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>
              {s.items.map((it, ii) => (
                <ChecklistFieldEditor key={it.id} item={it} idx={ii} testid={`form-item-${si}-${ii}`} isFirst={ii === 0} isLast={ii === s.items.length - 1}
                  onChange={(v) => setItems(si, (items) => items.map((x, j) => (j === ii ? v : x)))}
                  onDelete={() => setItems(si, (items) => items.filter((_, j) => j !== ii))}
                  onMove={(d) => setItems(si, (items) => move(items, ii, d))} />
              ))}
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" data-testid={`form-add-item-${si}`} onClick={() => setItems(si, (items) => [...items, newItem("checkbox")])}><Plus className="h-3.5 w-3.5 mr-1" />Tick box</Button>
                <Button size="sm" variant="outline" data-testid={`form-add-text-${si}`} onClick={() => setItems(si, (items) => [...items, newItem("short_text")])}><Plus className="h-3.5 w-3.5 mr-1" />Text</Button>
                <Button size="sm" variant="outline" data-testid={`form-add-dropdown-${si}`} onClick={() => setItems(si, (items) => [...items, { ...newItem("dropdown"), options: ["Option 1", "Option 2"] }])}><Plus className="h-3.5 w-3.5 mr-1" />Drop-down</Button>
                <Button size="sm" variant="ghost" data-testid={`form-add-other-${si}`} onClick={() => setItems(si, (items) => [...items, newItem("yes_no")])}><Plus className="h-3.5 w-3.5 mr-1" />Other field</Button>
              </div>
            </div>
          ))}
          <Button variant="outline" data-testid="form-add-section" onClick={() => setForm((f) => ({ ...f, sections: [...f.sections, { id: uid(), title: "", items: [newItem()] }] }))}><Plus className="h-4 w-4 mr-1" />Add section</Button>
          <div className="sticky bottom-0 flex flex-wrap justify-end gap-2 border-t bg-background pt-4 pb-2">
            <Button variant="ghost" size="lg" onClick={() => onOpenChange(false)} data-testid="form-cancel-btn">Cancel</Button>
            <Button variant="outline" size="lg" onClick={() => save(false)} disabled={busy || !form.name.trim()} data-testid="form-save-btn">Save draft</Button>
            <Button size="lg" onClick={() => save(true)} disabled={busy || !form.name.trim()} className="bg-brand hover:bg-brand-dark" data-testid="form-save-publish-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & publish"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
